/**
 * `/sessions/:id/building`: "Bâtiment" — every floor and door of the
 * building holding the caller's current door.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getBuilding, getSession, nextDoor } from '../../../api/sessions';
import { Button, Skeleton, useToast } from '../../../components/ui';
import { t } from '../../../i18n/fr';
import { FloorList } from '../components/FloorList';
import { ProgressBar } from '../components/ProgressBar';
import { SessionBanner } from '../components/SessionBanner';
import { sessionErrorMessage } from '../errors';

import type { Building, SessionWithExtras } from '../../../api/types';

/**
 * Render the "Bâtiment" screen.
 *
 * @returns The page element.
 */
export function BuildingPage() {
  const { id } = useParams<{ id: string }>();
  const sessionId = Number(id);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [session, setSession] = useState<SessionWithExtras | null>(null);
  const [building, setBuilding] = useState<Building | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([getSession(sessionId), getBuilding(sessionId)])
      .then(([sessionData, buildingData]) => {
        if (!cancelled) {
          setSession(sessionData);
          setBuilding(buildingData);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          showToast(sessionErrorMessage(error), 'error');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, showToast]);

  async function handleFinishFloor() {
    setBusy(true);
    try {
      await nextDoor(sessionId);
    } catch {
      // Whatever went wrong (including `no_door_left`), the guidance
      // screen is the right place to show it — it re-requests the door.
    } finally {
      setBusy(false);
      navigate(`/sessions/${sessionId}`);
    }
  }

  if (loading) {
    return (
      <div className="pile" aria-hidden="true">
        <Skeleton height="3rem" />
        <Skeleton height="16rem" />
      </div>
    );
  }

  if (!session || !building) {
    return null;
  }

  const addressLine = building.address.building_name
    ? `${building.address.label} — ${building.address.building_name}`
    : building.address.label;

  return (
    <div className="pile">
      <SessionBanner
        name={session.name}
        participantsCount={session.participants.length}
        progress={session.progress}
        onClick={() => navigate('/sessions/join')}
      />
      <div className="bloc">
        <div className="adr" style={{ fontSize: 'var(--text-xl)' }}>
          {addressLine}
        </div>
        <p className="muted" style={{ margin: '0 0 var(--space-2)' }}>
          {t('session.building.lead')}
        </p>
        <ProgressBar
          value={building.progress.recorded}
          total={building.progress.total}
          label={`${building.progress.recorded} / ${building.progress.total}`}
        />
      </div>
      <FloorList building={building} onSelectMine={() => navigate(`/sessions/${sessionId}/door`)} />
      <div className="bloc">
        <Button
          variant="primary"
          fullWidth
          disabled={busy}
          onClick={() => void handleFinishFloor()}
        >
          {t('session.building.finishFloor')}
        </Button>
      </div>
    </div>
  );
}
